import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Send } from 'lucide-react';
import { usePublicSiteSettings } from '@/hooks/usePublicSiteSettings';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSubmitLead } from '@/hooks/useSubmitLead';
import { toast } from 'sonner';
import logo from '@/assets/logo.png';

export function Footer() {
  const { data: settings } = usePublicSiteSettings();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const { submitLead, isSubmitting } = useSubmitLead();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    // Client-side validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Email inválido');
      return;
    }
    
    const result = await submitLead({
      name: 'Newsletter',
      email: email.trim(),
      source: 'footer_newsletter',
    });

    if (result.success) {
      toast.success('Inscrito com sucesso!');
      setEmail('');
      setWebsite('');
    } else if (result.isRateLimited) {
      toast.warning(result.message);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <footer className="bg-[#0a0a0a] text-white">
      <div className="container-custom py-10 md:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 md:gap-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* Logo & Tagline */}
          <div className="space-y-4 md:space-y-6 text-center sm:text-left">
            <Link to="/" className="inline-block">
              <img 
                src={logo} 
                alt="Logo" 
                width={145}
                height={40}
                className="h-10 md:h-14 w-auto brightness-0 invert mx-auto sm:mx-0"
              />
            </Link>
            
            {/* Gold Accent Line */}
            <div className="h-0.5 w-12 bg-primary mx-auto sm:mx-0" />
            
            <p className="text-xs md:text-sm text-gray-400 leading-relaxed max-w-xs mx-auto sm:mx-0">
              CRECI: 233.298-F
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4 md:space-y-6 text-center sm:text-left">
            <div className="text-xs md:text-sm font-semibold tracking-wider text-white uppercase">
              Contato
            </div>
            <div className="space-y-3 md:space-y-4">
              {settings?.phone && (
                <a
                  href={`tel:55${settings.phone.replace(/\D/g, '')}`}
                  className="block text-xs md:text-sm text-gray-400 transition-colors hover:text-white"
                >
                  {settings.phone}
                </a>
              )}
              {settings?.email && (
                <a
                  href={`mailto:${settings.email}`}
                  className="block text-xs md:text-sm text-gray-400 transition-colors hover:text-white"
                >
                  {settings.email}
                </a>
              )}
              {settings?.address && (
                <p className="text-xs md:text-sm text-gray-400 max-w-xs mx-auto sm:mx-0">
                  {settings.address}
                  {settings.city && <><br />{settings.city}{settings.state ? ` - ${settings.state}` : ''}</>}
                </p>
              )}
            </div>

            {/* Social Icons */}
            <div className="flex gap-3 pt-2 justify-center sm:justify-start">
              {settings?.instagram && (
                <a
                  href={settings.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-sm border border-gray-700 text-gray-400 transition-colors hover:border-primary hover:text-primary"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              )}
              {settings?.facebook && (
                <a
                  href={settings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-sm border border-gray-700 text-gray-400 transition-colors hover:border-primary hover:text-primary"
                  aria-label="Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {settings?.linkedin && (
                <a
                  href={settings.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-sm border border-gray-700 text-gray-400 transition-colors hover:border-primary hover:text-primary"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>

          {/* Newsletter */}
          <div className="space-y-4 md:space-y-6 text-center sm:text-left sm:col-span-2 lg:col-span-1">
            <div className="text-xs md:text-sm font-semibold tracking-wider text-white uppercase">
              Newsletter
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-0 max-w-sm mx-auto sm:mx-0">
              {/* Honeypot field - hidden from users */}
              <div className="absolute left-[-9999px]" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              
              <Input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={120}
                className="h-10 md:h-12 flex-1 rounded-none border-gray-700 bg-transparent text-xs md:text-sm text-white placeholder:text-gray-500 focus-visible:ring-0 focus-visible:border-gray-500"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={isSubmitting}
                aria-label="Inscrever-se na newsletter"
                className="h-10 w-10 md:h-12 md:w-12 rounded-none bg-primary hover:bg-primary/90"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container-custom flex flex-col items-center justify-between gap-3 md:gap-4 py-4 md:py-6 px-4 sm:px-6 lg:px-8 md:flex-row">
          <p className="text-[10px] md:text-xs text-gray-400 text-center md:text-left">
            © 2026 Guilherme Cavalheiro CRECI: 233.298-F. Todos os direitos reservados.
          </p>
          <a 
            href="https://agnrx.com" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] md:text-xs text-gray-400 transition-colors hover:text-gray-300"
          >
            Desenvolvido com amor e café por RX®
          </a>
        </div>
      </div>
    </footer>
  );
}